import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import strings from "translate/language";
import { visitorDetail } from "reduxStore/action/visitorActions";
import { useDispatch, useSelector } from "react-redux";
import {
  getAge,
  getCPLeadType,
  handleDetailResponse,
  hideMobileNumber,
  isJSON,
  seo,
} from "utils/utils";
import moment from "moment";
import Breadcrumb from "component/common/breadcrumb";
import { genderCheck, MIS_RoleId } from "utils/constants";
import usePermission from "utils/usePermission";
import { getStatusBadgeIconClass } from "utils/statusBadgeIcons";
import {
  getVisitorDisplayBadgeClass,
  resolveVisitorDisplayStatus,
} from "utils/visitorStatusDisplay";
import "./VisitorListing.css";

const VisitorStatusIcon = ({ badgeClass }) => (
  <span className="visitor-status-badge__icon" aria-hidden="true">
    <i className={getStatusBadgeIconClass(badgeClass)} />
  </span>
);

const displayValue = (value) => {
  if (value == null || value === "" || value === "NA") {
    return strings.notavailable;
  }
  return value;
};

const formatDate = (value) =>
  value ? moment(value).format("DD-MM-yyyy") : strings.notavailable;

const FactCell = ({ label, value }) => {
  const cleanLabel = String(label || "")
    .replace(/\s*:\s*$/, "")
    .trim();
  return (
    <div className="justo-visitor-detail__fact">
      <dt>{cleanLabel}</dt>
      <dd className={value === strings.notavailable ? "is-muted" : ""}>
        {displayValue(value)}
      </dd>
    </div>
  );
};

const toTitleCase = (text = "") =>
  String(text)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const FactSection = ({ title, rows }) => (
  <section className="justo-visitor-detail__section">
    <h2 className="justo-visitor-detail__section-title">{toTitleCase(title)}</h2>
    <dl className="justo-visitor-detail__facts">
      {rows.map((row) => (
        <FactCell key={row.label} label={row.label} value={row.value} />
      ))}
    </dl>
  </section>
);

const ViewVisitor = () => {
  const navigate = useNavigate();
  const [viewData, setViewData] = useState({});
  const params = useParams();
  const { _id: lead_id } = params || {};
  const dispatch = useDispatch();
  const { response: res } = useSelector((state) => state.login);
  const misRole = MIS_RoleId === res?.data?.role_id;

  const visitorDetailSelector = useSelector((state) => state.visitor);
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const isCreated = useSelector((state) => {
    const { response } = state.login;
    return viewData?.create_by === handleDetailResponse(response.data)._id
      ? true
      : false;
  });

  const { edit } = usePermission({ edit: "edit_visitor" });

  useEffect(() => {
    dispatch(visitorDetail({ lead_id: lead_id }));
  }, [dispatch, lead_id]);

  useEffect(() => {
    if (visitorDetailSelector.detail) {
      const { response } = visitorDetailSelector;
      if (response?.status === 200) {
        let office_address = {};
        const result = {
          ...handleDetailResponse(response.data),
          ...handleDetailResponse(response.data)?.customer_detail,
        };
        const typeofaddress = typeof result?.office_address;
        if (typeofaddress === "string" || typeofaddress === "object") {
          office_address =
            typeofaddress === "object"
              ? result?.office_address
              : isJSON(result?.office_address)
                ? JSON.parse(result?.office_address)
                : null;
        }
        setViewData(
          handleDetailResponse({ ...result, office_address: office_address }),
        );
      }
    }
  }, [visitorDetailSelector]);

  useEffect(() => {
    seo({
      title: "Visitor Details",
      metaDescription: "Visitor details",
      canonicalLink: window.location.href,
    });
  }, []);

  const capitalizeWords = (text = "") => {
    if (typeof text !== "string") {
      return "";
    }

    return text
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const formatAddress = (value) => {
    if (!value || value === "NA") return strings.notavailable;
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      const parts = Object.values(value).filter(Boolean);
      return parts.length ? parts.join(", ") : strings.notavailable;
    }
    return value;
  };

  const getMobileValue = () => {
    const mobile = viewData?.customer_detail?.mobile;
    if (!viewData?._id) return displayValue(mobile);
    if (isCreated) return displayValue(mobile);
    return mobile ? hideMobileNumber(mobile) : strings.notavailable;
  };

  const getAgeValue = () => {
    const birthDate = viewData?.customer_detail?.birth_date;
    if (!birthDate) return strings.notavailable;
    const age = getAge(birthDate);
    return isNaN(age) || age == null ? strings.notavailable : String(age);
  };

  const getBudgetValue = () => {
    if (viewData?.budget_amount) return viewData.budget_amount;
    if (viewData?.min_budget && viewData?.max_budget) {
      return `${viewData.min_budget}${viewData.min_budget_type} - ${viewData.max_budget}${viewData.max_budget_type}`;
    }
    return strings.notavailable;
  };

  const visitorName =
    `${viewData?.customer_detail?.first_name || ""} ${
      viewData?.customer_detail?.last_name || ""
    }`.trim() || strings.notavailable;
  const statusDisplay = resolveVisitorDisplayStatus(viewData);
  const statusBadgeClass = getVisitorDisplayBadgeClass(statusDisplay);
  const statusLabel = statusDisplay.label || strings.notavailable;
  const leadSource =
    viewData?.lead_source === "Reference" && viewData?.referrel_partner === 1
      ? "Referral Partner"
      : viewData?.lead_source || strings.notavailable;
  const cpLeadType = getCPLeadType(viewData?.cp_lead_type);
  const leadSourceDisplay = `${leadSource}${cpLeadType ? ` ${cpLeadType}` : ""}`;
  const showStatusHint =
    !viewData.draft &&
    Number(viewData?.lead_status) !== 5 &&
    Number(viewData?.lead_status) !== 6;
  const showEditVisitor =
    edit && !misRole && Number(viewData?.lead_status) <= 3;

  const sourceName =
    viewData?.created_name === "Mathan LMS"
      ? "Mathan LMS"
      : viewData?.created_name ||
        (viewData?.lms_lead_id &&
        typeof viewData.lms_lead_id === "string" &&
        viewData.lms_lead_id.trim() !== ""
          ? "Mathan LMS"
          : strings.notavailable);

  const qualifiedLabel =
    viewData?.qualified === true
      ? strings.qualified
      : viewData?.qualified === false
        ? strings.unQualified
        : strings.notavailable;

  const overviewRows = [
    { label: strings.propertyName, value: viewData?.property_title },
    { label: strings.lastInteracted, value: formatDate(viewData?.last_interacted_date) },
    { label: strings.source, value: sourceName },
    { label: strings.status, value: statusLabel },
    { label: strings.leadSource, value: leadSourceDisplay },
    { label: strings.visitorStatusFilter, value: qualifiedLabel },
    {
      label: strings.leadPriority,
      value: capitalizeWords(viewData?.lead_priority) || strings.notavailable,
    },
    { label: strings.visitScore, value: viewData?.lead_score },
  ];

  if (viewData?.lead_source === "Reference") {
    overviewRows.push({
      label: strings.referrer,
      value: viewData?.referrer_name,
    });
  }

  if (viewData?.leadSource === "Channel Partner") {
    overviewRows.push({
      label: viewData?.cp_type === 2 ? "CP Company Name" : "CP Name",
      value: viewData?.cp_name,
    });
  }

  if (viewData?.cp_type === 2) {
    overviewRows.push({
      label: strings.emname,
      value: viewData?.cp_emp_name,
    });
  }

  const propertyRows = [
    { label: strings.configuration, value: viewData?.configuration },
    { label: strings.Area, value: viewData?.areain_sqlft },
    { label: strings.budget, value: getBudgetValue() },
    { label: strings.natureOfFunding, value: viewData?.funding_type },
    { label: strings.purpose, value: viewData?.purpose },
  ];

  const customerRows = [
    { label: strings.visitorName, value: visitorName },
    { label: strings.mobileNo, value: getMobileValue() },
    { label: strings.ethniCity, value: viewData?.customer_detail?.ethnicity },
    {
      label: strings.visitingWith,
      value:
        capitalizeWords(viewData?.customer_detail?.visit_with) ||
        strings.notavailable,
    },
    {
      label: strings.nameOfOccupation,
      value:
        capitalizeWords(viewData?.customer_detail?.occupation) ||
        strings.notavailable,
    },
    {
      label: strings.residentLocation,
      value: viewData?.customer_detail?.location,
    },
    {
      label: strings.workLocation,
      value: formatAddress(viewData?.customer_detail?.office_address),
    },
    { label: strings.age, value: getAgeValue() },
    {
      label: strings.gender,
      value:
        viewData?.customer_detail?.gender === null
          ? strings.notavailable
          : genderCheck[viewData?.customer_detail?.gender],
    },
    { label: strings.locality, value: viewData?.customer_detail?.locality },
  ];

  return (
    <div className="page-body justo-visitor-page">
      <Breadcrumb
        title={strings.visitorDetails}
        url={"/visitor"}
        parentListing={strings.visitorList}
        titleactive={strings.viewvisitordetails}
        isBackBtn={true}
      />

      <div className="container-fluid justo-visitor">
        <div className="justo-visitor__card justo-visitor-detail">
          <div className="justo-visitor-detail__hero">
            <div className="justo-visitor-detail__hero-main">
              <h1 className="justo-visitor-detail__property">
                {displayValue(viewData?.property_title)}
              </h1>
              <p className="justo-visitor-detail__meta">
                <span>
                  <strong>{visitorName}</strong>
                </span>
                <span>{displayValue(getMobileValue())}</span>
                <span>{displayValue(leadSource)}</span>
                <span>
                  {strings.visitScore}:{" "}
                  <strong>{displayValue(viewData?.lead_score)}</strong>
                </span>
              </p>
            </div>

            <div className="justo-visitor-detail__hero-actions">
              <span
                className={`visitor-status-badge ${statusBadgeClass}`}
                title={statusLabel}
              >
                <VisitorStatusIcon badgeClass={statusBadgeClass} />
                <span className="visitor-status-badge__label">{statusLabel}</span>
              </span>
              {showEditVisitor || showStatusHint ? (
                <div className="justo-visitor-detail__hero-cta">
                  <div className="justo-visitor-detail__hero-cta-row">
                    {showEditVisitor ? (
                      <button
                        className="justo-visitor__btn justo-visitor__btn--outline"
                        type="button"
                        onClick={() => {
                          const path =
                            viewData?.draft === true
                              ? `/visitor_form_draft?lead_id=${lead_id}`
                              : `/visitor_form?lead_id=${lead_id}`;
                          navigatpage(path);
                        }}
                      >
                        {strings.editVisitor}
                      </button>
                    ) : null}
                    {showStatusHint ? (
                      <button
                        className="justo-visitor__btn justo-visitor__btn--soft"
                        type="button"
                        title={strings.visitorStatusUpdateHint}
                        onClick={() => navigatpage("/appointment")}
                      >
                        <i className="fa fa-calendar" aria-hidden="true" />
                        {strings.openAppointmentManagement}
                      </button>
                    ) : null}
                  </div>
                  {showStatusHint ? (
                    <p className="justo-visitor-detail__status-hint">
                      {strings.visitorStatusUpdateHint}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="justo-visitor-detail__content">
            <div className="justo-visitor-detail__grid">
              <FactSection title={strings.visitorDetails} rows={overviewRows} />
              <FactSection
                title={strings.propertyRequired}
                rows={propertyRows}
              />
              <FactSection
                title={strings.customerDetails}
                rows={customerRows}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewVisitor;
